import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { pickLocale } from "../lib/format";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import ProductFilters, { clearProductFilters, getProductFilterQuery } from "../components/storefront/ProductFilters";
import ProductListingShell, { ProductListingResults } from "../components/storefront/ProductListingShell";
import SeoMeta from "../components/storefront/SeoMeta";
import { buildListingJsonLd } from "../lib/listingJsonLd";
import Pagination, { PAGE_SIZE } from "../components/storefront/Pagination";
import { listingHeroImage } from "../components/storefront/assets";
import { getCategories, getHeaderNavigationTree } from "../lib/publicConfig";
import { useI18n } from "../lib/i18n";
import { findNavSection, parseCategoryParam } from "../lib/categoryTree";
import { normaliseNavigation } from "../lib/navigationTree";
import CategorySubnav from "../components/storefront/CategorySubnav";

const labelFromHandle = (handle = "") => handle.split("-").map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ");

const categoryVisualIndex = {
  suits: 0,
  shirts: 1,
  shoes: 5,
  belts: 6,
  ties: 3,
  accessories: 5,
};

function categoryVisual(handle) {
  return listingHeroImage(categoryVisualIndex[handle] || 0);
}

function normaliseHeroSlides(value, fallback) {
  const rows = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value.split(/[\n,]+/)
      : [];
  const slides = rows.map((item) => String(item || "").trim()).filter(Boolean);
  return slides.length ? slides : [fallback].filter(Boolean);
}

function categoryIntro(handle, locale) {
  const az = {
    shirts: "Klassik yaxalı, gündəlik və mövsümə uyğun köynəkləri ölçü, rəng və fit üzrə seçin.",
    suits: "İş günü, tədbir və mərasim üçün kostyumları siluet və parça hissinə görə araşdırın.",
    shoes: "Look-u tamamlayan ayaqqabı seçimlərini rəng və formal istifadə üzrə müqayisə edin.",
    accessories: "Qalstuk, kəmər və tamamlayıcı detallarla hazır görünüşü tamamlayın.",
  };
  const en = {
    shirts: "Explore formal, casual and seasonal shirts by fit, colour and occasion.",
    suits: "Choose suits for business, events and ceremony by silhouette and cloth feel.",
    shoes: "Compare finishing footwear by colour, formality and use case.",
    accessories: "Complete the look with ties, belts and considered finishing details.",
  };
  return (locale === "az" ? az : en)[handle] || (locale === "az"
    ? "Məhsulları ölçü, rəng, material və istifadə məqsədinə görə süzün."
    : "Filter pieces by size, colour, material and intended use.");
}

export default function Category() {
  const { handle } = useParams();
  const { locale } = useI18n();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  // undefined = yüklənir · null = tapılmadı · {...} = kateqoriya
  const [category, setCategory] = useState(undefined);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let active = true;
    setCategory(undefined);
    api.get(`/categories/${handle}`)
      .then((r) => { if (active) setCategory(r.data); })
      .catch(() => { if (active) setCategory(null); });
    return () => { active = false; };
  }, [handle]);

  // Görünən kateqoriyalar (+ alt-ağac məhsul sayı): kökü, alt kateqoriya
  // sətrini və KATEQORİYALAR filtrini buradan qururuq.
  const [allCategories, setAllCategories] = useState([]);
  useEffect(() => {
    let active = true;
    getCategories()
      .then((rows) => { if (active) setAllCategories(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (active) setAllCategories([]); });
    return () => { active = false; };
  }, []);

  // Breadcrumb-ın bölmə pilləsi (Geyim) menyudan gəlir — Header eyni sorğunu
  // artıq edib, cavab keşdədir.
  const [navItems, setNavItems] = useState([]);
  useEffect(() => {
    let active = true;
    getHeaderNavigationTree()
      .then((rows) => { if (active) setNavItems(normaliseNavigation(rows)); })
      .catch(() => { if (active) setNavItems([]); });
    return () => { active = false; };
  }, []);

  /* Səhifənin kökü: alt kateqoriya səhifəsində (/categories/klassik-salvar)
     başlıq, link sətri və filtr ana kateqoriyanındır (Şalvarlar), cari alt
     kateqoriya isə aktiv bənddir. Valideyn gizlidirsə səhifə özü kökdür. */
  const parentRow = useMemo(() => {
    if (!category?.parent_id) return null;
    return allCategories.find((row) => row.id === category.parent_id) || null;
  }, [allCategories, category]);
  const root = parentRow || category || null;
  const isSub = Boolean(parentRow);
  const rootHandle = root?.handle || handle;

  const categoryParam = sp.get("category") || "";
  const selected = useMemo(() => {
    const extras = parseCategoryParam(categoryParam);
    return [...new Set([...(isSub ? [handle] : []), ...extras])];
  }, [categoryParam, handle, isSub]);

  const children = useMemo(() => {
    if (!root?.id) return [];
    // 0 məhsullu alt kateqoriya gizlədilir (tapşırıq); seçili olan isə həmişə
    // görünür ki, aktiv bənd yoxa çıxmasın.
    return allCategories.filter((row) => row.parent_id === root.id
      && (Number(row.product_count || 0) > 0 || selected.includes(row.handle)));
  }, [allCategories, root, selected]);

  const selectionHref = useCallback((list, baseSearch) => {
    const next = new URLSearchParams(baseSearch || sp);
    next.delete("category");
    next.delete("page");
    let path = `/categories/${rootHandle}`;
    if (list.length === 1) path = `/categories/${list[0]}`;
    else if (list.length > 1) next.set("category", list.join(","));
    const search = next.toString();
    return `${path}${search ? `?${search}` : ""}`;
  }, [rootHandle, sp]);

  const changeSelection = useCallback((list, baseSearch) => {
    navigate(selectionHref(list, baseSearch));
  }, [navigate, selectionHref]);

  /* `?category=a,b` yalnız kök məlum olanda mənalıdır: kök səhifəsində seçim
     onu əvəz edir, alt səhifədə ona əlavə olunur. Parametr yoxdursa sorğu
     birbaşa URL-in handle-ıdır — gözləməyə ehtiyac yoxdur. */
  const waitingForRoot = Boolean(categoryParam) && (category === undefined || !allCategories.length);
  const queryParams = useMemo(() => {
    const filters = getProductFilterQuery(sp);
    delete filters.category;
    return {
      ...filters,
      category: selected.length ? selected.join(",") : handle,
      page: Math.max(1, Number(sp.get("page")) || 1),
      page_size: PAGE_SIZE,
      sort: sp.get("sort") || "newest",
    };
  }, [handle, sp, selected]);
  const filterScope = useMemo(() => ({ category: selected.length ? selected.join(",") : rootHandle }), [selected, rootHandle]);

  useEffect(() => {
    if (waitingForRoot) return undefined;
    let active = true;
    setLoading(true);
    setLoadError(false);
    api.get("/products", { params: queryParams })
      .then((r) => {
        if (!active) return;
        const items = r.data.items || [];
        setProducts(items);
        setTotal(Number(r.data.total ?? items.length));
      })
      .catch(() => {
        if (!active) return;
        setProducts([]);
        setTotal(0);
        setLoadError(true);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [queryParams, retryKey, waitingForRoot]);

  const listingIsEmpty = !loading && !loadError && products.length === 0;
  // Same rule as the collection page: nothing to show, nothing to index.
  const seo = {
    ...(category?.seo ?? {}),
    ...(listingIsEmpty || category?.seo?.menu_visible === false ? { index: false } : {}),
  };
  // Şəkil/redaksiya açarları alt səhifədə özündə yoxdursa kökdən gəlir.
  const visualSeo = isSub ? { ...(root?.seo ?? {}), ...seo } : seo;
  const rootTitle = root ? pickLocale(root.seo?.h1, locale) || pickLocale(root.title, locale) : "";
  const ownTitle = category ? pickLocale(seo.h1, locale) || pickLocale(category.title, locale) : "";
  const title = isSub
    ? rootTitle
    : ownTitle || (category === null ? labelFromHandle(handle) : "");
  const documentTitle = isSub && !category?.seo_title
    ? `${pickLocale(category?.title, locale)} — ${rootTitle}`
    : (category?.seo_title || category?.title);
  const description = pickLocale(category?.description, locale)
    || pickLocale(seo.seo_intro, locale)
    || (isSub ? pickLocale(root?.description, locale) : "");
  const section = findNavSection(navItems, rootHandle);
  const breadcrumbs = [
    section && { label: pickLocale(section.title, locale), to: section.to },
    root && { label: rootTitle, to: isSub || selected.length ? `/categories/${rootHandle}` : null },
    isSub && { label: pickLocale(category?.title, locale) },
  ].filter(Boolean);
  const subnavItems = children.map((child) => ({
    key: child.handle,
    label: pickLocale(child.title, locale),
    to: selectionHref([child.handle]),
    active: selected.includes(child.handle),
    count: Number(child.product_count || 0),
  }));
  // Kökün sayı görünən siyahıdandır (/categories/{handle} cavabında say yoxdur).
  const rootCount = Number(allCategories.find((row) => row.id === root?.id)?.product_count || 0);
  const categoryGroup = children.length ? {
    root: rootHandle,
    selected,
    options: children.map((child) => ({ value: child.handle, label: pickLocale(child.title, locale) })),
    onChange: changeSelection,
  } : null;
  const categoryImage = visualSeo.category_image;
  const heroImage = visualSeo.listing_hero_image || categoryImage || categoryVisual(rootHandle);
  const heroSlides = normaliseHeroSlides(visualSeo.listing_hero_slides, heroImage);
  const heroImageUrl = typeof heroImage === "string" ? heroImage : heroImage?.src;
  const helperText = description || categoryIntro(rootHandle, locale);
  const heroNote = pickLocale(visualSeo.listing_hero_note, locale);
  const editorialEyebrow = pickLocale(visualSeo.landing_editorial_eyebrow, locale);
  const editorialTitle = pickLocale(visualSeo.landing_editorial_title, locale);
  const editorialText = pickLocale(visualSeo.landing_editorial_text, locale);
  const editorialCta = pickLocale(visualSeo.landing_editorial_cta_label, locale);
  const bottomContent = pickLocale(visualSeo.bottom_content, locale);
  /* `menu_visible: false` is a menu instruction, not a takedown. It used to
     redirect the whole landing page to /collections, so an editor tidying the
     navigation silently destroyed the shelf — and any link or ad pointing at
     it. The `logix-*` handles stay redirected: the importer that minted them is
     gone, but its rows are still in `categories` and were never a shopper's
     idea of a shelf, so un-redirecting them would publish hundreds of scratch
     landing pages. This goes when those rows go, not before. */
  if (handle?.startsWith("logix-")) {
    return <Navigate to="/shop" replace />;
  }
  const hiddenFromMenu = seo.menu_visible === false;

  return (
    <div className="bg-[var(--adf-white)] text-[var(--adf-ink)]" data-testid={`category-${handle}`}>
      <SeoMeta
        title={documentTitle}
        description={category?.seo_description || category?.description}
        seo={seo}
        locale={locale}
        canonicalPath={`/categories/${handle}`}
        image={heroImageUrl}
        jsonLd={buildListingJsonLd({
          kind: "category", handle, title, description, products, locale,
          label: locale === "az" ? "Kataloq" : "Shop",
        })}
      />
      <Header />
      <ProductListingShell
        title={title}
        eyebrow={editorialEyebrow || (locale === "az" ? "Kateqoriya" : "Category")}
        description={helperText}
        image={heroImage}
        heroSlides={heroSlides}
        imageFit={visualSeo.listing_hero_image_fit || "contain"}
        imagePosition={visualSeo.listing_hero_image_position || "center"}
        imageHeight={visualSeo.listing_hero_image_height || 420}
        heroNote={heroNote}
        breadcrumbs={breadcrumbs}
        align="center"
        subnav={subnavItems.length > 0 ? (
          <CategorySubnav
            items={subnavItems}
            allTo={selectionHref([])}
            allActive={selected.length === 0}
            allCount={rootCount}
          />
        ) : null}
        total={total}
        loading={loading}
        toolbar={(
          <ProductFilters
            searchParams={sp}
            setSearchParams={setSp}
            total={total}
            loading={loading}
            scopeParams={filterScope}
            categoryGroup={categoryGroup}
          />
        )}
        testId={`category-listing-${handle}`}
      >
        <ProductListingResults
          listName="category"
          products={products}
          loading={loading}
          loadError={loadError}
          onRetry={() => setRetryKey((value) => value + 1)}
          onClear={() => clearProductFilters(sp, setSp)}
          productTestId="category-product"
          emptyActions={<Link to="/shop" className="adf-cta adf-cta-outline">{locale === "az" ? "Bütün məhsullara bax" : "View all products"}</Link>}
        />

        <Pagination
          page={queryParams.page}
          total={total}
          onChange={(next) => {
            const spNext = new URLSearchParams(sp);
            if (next <= 1) spNext.delete("page"); else spNext.set("page", String(next));
            setSp(spNext);
            document.getElementById("main")?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          testId="category-pagination"
        />

        {(editorialTitle || editorialText) && (
          <section className="grid gap-8 border-t border-[var(--adf-gray-5)] py-16 md:grid-cols-[0.8fr_1.2fr] md:py-24">
            <div className="adf-small text-[var(--adf-gray-2)]">{editorialEyebrow || "Editorial"}</div>
            <div className="max-w-2xl">
              {editorialTitle && <h2 className="adf-heading text-[42px] md:text-[58px]">{editorialTitle}</h2>}
              {editorialText && <p className="mt-6 text-[15px] leading-7 text-[var(--adf-gray-2)]">{editorialText}</p>}
              {editorialCta && seo.landing_editorial_cta_url && (
                <Link to={seo.landing_editorial_cta_url} className="adf-cta adf-cta-outline mt-8">{editorialCta}</Link>
              )}
            </div>
          </section>
        )}
        {bottomContent && (
          <section className="border-t border-[var(--adf-gray-5)] py-12 text-[14px] leading-7 text-[var(--adf-gray-2)]">
            <div className="max-w-3xl">{bottomContent}</div>
          </section>
        )}
      </ProductListingShell>
      <Footer />
    </div>
  );
}
